import React from "react"
import { graphql } from "gatsby"
import { NotFoundPageQuery } from "../../../graphql-types"
import * as styles from "./404.module.scss"
import * as stylesTypePage from "../type-page/type-page.module.scss"
import { useFormatLocale } from "../../hooks"
import { RelativeLanguagePaths } from "../../components/layout/header/header-tool-bar/language-selector/language-selector"
import Layout from "../../components/layout/layout"
import SEO from "../../components/seo"
import Breadcrumb from "../../components/type-page/breadcrumb/breadcrumb"
import TitleBanner from "../../components/type-page/title-banner/title-banner"
import LoadScripts from "../../components/load-scripts"

type RenderProps = {
  data: NotFoundPageQuery
  location: Location
  pageContext: {
    locale: string
    defaultLocale: string
    relativeLanguagePaths: RelativeLanguagePaths
  }
}

const toInternalHref = (slug?: string | null) => {
  if (!slug) {
    return "/"
  }
  return slug.startsWith("/") ? slug : `/${slug}`
}

const NotFoundText: React.FC<{ locale: string }> = ({ locale }) => {
  const getTextContent = () => {
    switch (locale) {
      case "en":
        return {
          title: "Oops! This page isn't available.",
          subtitle: "Please see the information below.",
        }
      case "ga":
        return {
          title: "Úps! Níl an leathanach seo ar fáil.",
          subtitle: "Féach ar an eolas thíos, le do thoi.",
        }
      case "fr":
      default:
        return {
          title: "Oups ! Cette page n'est pas disponible",
          subtitle: "Nous vous proposons de consulter les informations suivantes.",
        }
    }
  }

  const { title, subtitle } = getTextContent()

  return (
    <div className={`${stylesTypePage.content} ${styles.content}`}>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.subtitle}>{subtitle}</p>
    </div>
  )
}

const NotFoundPage: React.FC<RenderProps> = ({ data, location, pageContext }) => {
  const locale = useFormatLocale(pageContext.locale)

  const getTitleText = () => {
    switch (locale) {
      case "en":
        return "ERROR 404"
      case "ga":
        return "EARRÁID 404"
      case "fr":
      default:
        return "ERREUR 404"
    }
  }

  const titleText = getTitleText()

  return (
    <Layout
      location={location}
      menuData={data.datoCmsMenu}
      footerData={data.datoCmsFooter}
      toolbarData={data.datoCmsGlobalConfiguration}
      scriptsData={data.datoCmsGlobalConfiguration}
      locale={useFormatLocale(pageContext.locale)}
      defaultLocale={useFormatLocale(pageContext.defaultLocale)}
      relativeLanguagePaths={pageContext.relativeLanguagePaths}
      mySubscriptionsPage={data.datoCmsMySubscriptionsPage}
    >
      <div className={stylesTypePage.typePage}>
        <TitleBanner title={titleText} />
        <Breadcrumb
          menuData={data.datoCmsMenu}
          pageTitle="404"
          locale={useFormatLocale(pageContext.locale)}
          defaultLocale={useFormatLocale(pageContext.defaultLocale)}
        />
        <div className={`${stylesTypePage.typePageContent} ${styles.wrapper}`}>
          <NotFoundText locale={locale} />
          <ul className={styles.linkBlocks}>
            {data.datoCmsNotFound.notFoundBlock.map((block, index) => {
              const title = (block.title || "").trim()
              if (!title) {
                return null
              }
              const internalLink = block.internalLink[0]
              const href =
                internalLink && internalLink.__typename === "DatoCmsPage" ? toInternalHref(internalLink.slug) : "/"
              return (
                <li key={index}>
                  <a href={href}>
                    <span className={styles.link}>{title}</span>
                  </a>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </Layout>
  )
}

export const Head: React.FC<RenderProps> = ({ data, pageContext }) => {
  return (
    <>
      <SEO title="404: Page introuvable" locale={pageContext.locale} />
      <LoadScripts scriptData={data.datoCmsGlobalConfiguration} />
    </>
  )
}

export const pageQuery = graphql`
  query notFoundPage($locale: String!) {
    site {
      siteMetadata {
        title
      }
    }
    datoCmsMySubscriptionsPage {
      ...MySubscriptionsPageFields
    }
    datoCmsMenu(locale: $locale) {
      ...HeaderMenuFields
      ...FooterMenuFields
    }

    datoCmsFooter(locale: $locale) {
      ...FooterFields
    }

    datoCmsGlobalConfiguration(locale: $locale) {
      ...HeaderToolBarFields
      ...ScriptsFields
    }

    datoCmsNotFound {
      notFoundBlock {
        title
        internalLink {
          ... on DatoCmsPage {
            __typename
            slug
          }
          ... on DatoCmsHome {
            __typename
          }
        }
      }
    }
  }
`

export default NotFoundPage
